import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import Input from '@/components/Input.jsx';
import ModalV2 from '@/components/ModalV2.jsx';
import Note from '@/components/Note.jsx';
import Select from '@/components/Select.jsx';
import { getAppByIdAndVersion } from '@/redux/actions/gravityActions.js';
import selectors from '@/redux/selectors/selectors.js';
import { APP_FLAW_SLUGS } from '@shared/schema/appFlawCatalog.js';
import buildFixerActionOptions from './lib/buildFixerActionOptions.js';
import buildFixerAppOptions from './lib/buildFixerAppOptions.js';
import requestAppFixer from './lib/requestAppFixer.js';

const reportRequiredMessage = 'App, action, and description are required.';
const categoryOptions = APP_FLAW_SLUGS.map((slug) => ({ value: slug, label: slug }));

const FixerReportModal = ({ show, initialAppId, initialActionId, disabled, onHide, onChanged }) => {
  const appsMetadata = useSelector(selectors.appsMetadata);
  const appOptions = useMemo(() => buildFixerAppOptions(appsMetadata), [appsMetadata]);
  const [appId, setAppId] = useState(initialAppId || null);
  const [actionId, setActionId] = useState(initialActionId || null);
  const [actionOptions, setActionOptions] = useState([]);
  const [loadingActions, setLoadingActions] = useState(false);
  const descriptionRef = useRef(null);
  const [flawSlug, setFlawSlug] = useState('OTHER');
  const expectedRef = useRef(null);
  const actualRef = useRef(null);
  const [credentialId, setCredentialId] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('info');
  const mounted = useRef(false);
  const selectedApp = appOptions.find((option) => option.value === appId) || null;
  const appVersion = appsMetadata?.find((app) => app.id === appId)?.publishedVersion;

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // Action ids only exist inside the app definition, so the action list follows the
  // selected app's published version instead of the metadata row.
  useEffect(() => {
    if (!show || !appId) {
      return undefined;
    }
    let active = true;
    setLoadingActions(true);
    setActionOptions([]);
    getAppByIdAndVersion({ appId, appVersion: appVersion || 'latest' }, (data, failure) => {
      if (!active) {
        return;
      }
      setLoadingActions(false);
      if (failure) {
        setMessage(failure.message || 'Could not load this app actions.');
        setMessageType('error');
        return;
      }
      const options = buildFixerActionOptions(Array.isArray(data) ? data[0] : data);
      setActionOptions(options);
      setActionId((current) => (options.some((option) => option.value === current) ? current : null));
    });
    return () => {
      active = false;
    };
  }, [show, appId, appVersion]);

  const handleAppChange = (option) => {
    setAppId(option?.value || null);
    setActionId(null);
    setCredentialId('');
    setMessage('');
  };
  const submit = () => {
    const description = descriptionRef.current?.value || '';
    const expected = expectedRef.current?.value || '';
    const actual = actualRef.current?.value || '';
    if (!appId || !actionOptions.some((option) => option.value === actionId) || !description.trim()) {
      setMessage(reportRequiredMessage);
      setMessageType('error');
      return;
    }
    setBusy(true);
    setMessage('');
    requestAppFixer({
      path: 'reports',
      data: { appId, actionId, flawSlug, description, expected, actual, ...(credentialId.trim() ? { credentialId: credentialId.trim() } : {}) },
      callback: (_result, failure) => {
        if (!mounted.current) {
          if (!failure) {
            onChanged?.();
          }
          return;
        }
        setBusy(false);
        if (failure) {
          setMessage(failure.message || 'Could not record this report.');
          setMessageType('error');
          return;
        }
        onHide();
        onChanged?.();
      }
    });
  };
  const actions = [
    {
      children: 'Submit report',
      className: 'primary',
      onClick: submit,
      loading: busy,
      disabled: disabled || busy
    }
  ];

  return (
    <ModalV2 //
      className="fixer-report-modal"
      centered={true}
      maxHeight="98svh"
      show={show}
      title="Report an app problem"
      description="Record a report for diagnosis. A matching report can start a repair automatically."
      onHide={onHide}
      actions={actions}
      message={message}
      messageType={messageType}>
      {disabled && (
        <div className="fixer-report-modal-alert">
          <Note type="warning" text="App Fixer is disabled. Reports cannot be filed until it is enabled in the service configuration." />
        </div>
      )}
      <Select className="fixer-report-app" label="App" options={appOptions} value={selectedApp} placeholder="Select an app" showOptionIcon={true} showReload={false} onChange={handleAppChange} floatingSelectV2Props={{ minWidth: 320, placeholder: 'Search apps', virtualize: true }} />
      <Select className="fixer-report-action" label="Action" options={actionOptions} value={actionOptions.find((option) => option.value === actionId) || null} placeholder={appId ? 'Select an action' : 'Select an app first'} disabled={!appId} loading={loadingActions} showReload={false} onChange={(option) => setActionId(option?.value || null)} floatingSelectV2Props={{ minWidth: 320, placeholder: 'Search actions', virtualize: true }} />
      <Input type="textarea" id="description" label="Description" maxLength={4000} defaultValue="" ref={descriptionRef} rows={3} placeholder="What did the app action do wrong?" />
      <Select className="fixer-report-category" label="Category" options={categoryOptions} value={categoryOptions.find((option) => option.value === flawSlug)} showReload={false} onChange={(option) => setFlawSlug(option.value)} floatingSelectV2Props={{ minWidth: 320, placeholder: 'Search categories' }} />
      <Input id="credentialId" label="Credential ID (optional)" description="Leave empty to use the app's agent connection." maxLength={256} value={credentialId} onChange={(event) => setCredentialId(event.target.value)} />
      <Input type="textarea" id="expected" label="Expected result" maxLength={2000} defaultValue="" ref={expectedRef} rows={3} />
      <Input type="textarea" id="actual" label="Actual result" maxLength={2000} defaultValue="" ref={actualRef} rows={3} />
      <p className="fixer-report-hint">Use a short description. Do not include passwords, API keys, or customer data.</p>
    </ModalV2>
  );
};

export default FixerReportModal;
